import React, { useEffect, useMemo, useRef, useState } from "react";
import { t } from "../../services/HelperService";
import { usePosition } from "../AccentDisplay";

export default function ServiceWindowBanner({
    isOpen,
    secondsRemaining,          // number (from backend)
    expiresAtUtc,              // ISO string (optional, for tooltip)
    onBannerSizeChanged,       // optional callback like your other banners
}) {
    const [remaining, setRemaining] = useState(secondsRemaining ?? 0);
    const bannerRef = useRef(null);
    usePosition("servicewindowbanner"); // keeps consistent with Trial/Message banner pattern

    // keep the local countdown ticking
    useEffect(() => {
        setRemaining(secondsRemaining ?? 0);
        if (!isOpen) return;

        const id = setInterval(() => {
            setRemaining((s) => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(id);
    }, [isOpen, secondsRemaining]);

    // report height (same approach your banners use)
    useEffect(() => {
        if (bannerRef.current && onBannerSizeChanged) {
            onBannerSizeChanged(bannerRef.current.clientHeight);
        }
    });

    const cls = useMemo(() => {
        if (!isOpen || remaining <= 0) return "chat-window-banner closed";
        if (remaining <= 10 * 60) return "chat-window-banner crit";   // < 10m
        if (remaining <= 60 * 60) return "chat-window-banner warn";   // < 60m
        return "chat-window-banner ok";
    }, [isOpen, remaining]);

    const format = (s) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        return h ? `${h}h ${m}m ${sec}s` : `${m}m ${sec}s`;
    };

    const title = expiresAtUtc
        ? t("application_strings.views.chat.serviceWindowBanner.timeRemaining", { date: new Date(expiresAtUtc).toLocaleString() })
        : t("application_strings.views.chat.serviceWindowBanner.timeRemainingFallback");

    if (!isOpen && remaining <= 0) {
        return (
            <div ref={bannerRef} className="chat-window-banner closed" role="status">
                {t("application_strings.views.chat.serviceWindowBanner.closed")}
            </div>
        );
    }

    return (
        <div ref={bannerRef} className={cls} role="status" aria-live="polite" title={title}>
            {t("application_strings.views.chat.serviceWindowBanner.open", { time: format(remaining) })}
        </div>
    );
}
